import { useEffect, useState, createElement } from "react";
import api from "@/lib/api";
import { Plus, Trash, Folder } from "@phosphor-icons/react";

const DEFAULT_META = {
  fields: [
    { name: "email", kind: "string", needs_source: false },
    { name: "first_name", kind: "string", needs_source: false },
    { name: "last_name", kind: "string", needs_source: false },
    { name: "company_name", kind: "string", needs_source: false },
    { name: "job_title", kind: "string", needs_source: false },
    { name: "country", kind: "string", needs_source: false },
    { name: "consent_status", kind: "enum", needs_source: false },
    { name: "source_type", kind: "enum", needs_source: true },
    { name: "lead_status", kind: "string", needs_source: true },
  ],
  ops: ["equals", "not_equals", "contains", "not_contains", "starts_with",
        "ends_with", "is_set", "not_set", "in", "not_in"],
  enums: {
    consent_status: ["unknown", "opt_in", "pending", "opt_out"],
    source_type: ["csv_import", "client", "crm_lead"],
  },
};

const OP_LABELS = {
  equals: "is",
  not_equals: "is not",
  contains: "contains",
  not_contains: "does not contain",
  starts_with: "starts with",
  ends_with: "ends with",
  is_set: "is set",
  not_set: "is empty",
  in: "is any of",
  not_in: "is none of",
};
const OPS_NO_VALUE = new Set(["is_set", "not_set"]);
const OPS_MULTI = new Set(["in", "not_in"]);

export function emptyTree() {
  return {
    logic: "AND",
    rules: [{ field: "country", op: "equals", value: "" }],
  };
}

function RuleRow({ meta, rule, onChange, onRemove }) {
  const field = meta.fields.find((f) => f.name === rule.field) || meta.fields[0];
  const enums = meta.enums[field.name];
  const needsVal = !OPS_NO_VALUE.has(rule.op);
  const multi = OPS_MULTI.has(rule.op);

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="filter-rule">
      <select
        value={rule.field}
        onChange={(e) => onChange({ ...rule, field: e.target.value, value: "" })}
        className="h-8 px-2 text-xs border border-[#E5E5E5] bg-white"
        data-testid="rule-field"
      >
        {meta.fields.map((f) => (
          <option key={f.name} value={f.name}>{f.name}</option>
        ))}
      </select>
      <select
        value={rule.op}
        onChange={(e) => {
          const nextOp = e.target.value;
          onChange({ ...rule, op: nextOp, value: OPS_NO_VALUE.has(nextOp) ? "" : rule.value });
        }}
        className="h-8 px-2 text-xs border border-[#E5E5E5] bg-white"
        data-testid="rule-op"
      >
        {meta.ops.map((o) => (
          <option key={o} value={o}>{OP_LABELS[o] || o}</option>
        ))}
      </select>
      {needsVal && enums && !multi ? (
        <select
          value={rule.value || ""}
          onChange={(e) => onChange({ ...rule, value: e.target.value })}
          className="h-8 px-2 text-xs border border-[#E5E5E5] bg-white min-w-[120px]"
          data-testid="rule-value"
        >
          <option value="">—</option>
          {enums.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      ) : null}
      {needsVal && (!enums || multi) ? (
        <input
          value={rule.value || ""}
          onChange={(e) => onChange({ ...rule, value: e.target.value })}
          placeholder={multi ? "comma,separated" : "value"}
          className="h-8 px-2 text-xs border border-[#E5E5E5] bg-white min-w-[120px]"
          data-testid="rule-value"
        />
      ) : null}
      <button
        onClick={onRemove}
        className="text-[#525252] hover:text-[#DC2626] p-1"
        data-testid="rule-remove"
      >
        <Trash size={12} />
      </button>
    </div>
  );
}

// NOTE: Written without self-referential JSX (`<RuleGroup .../>` inside RuleGroup)
// because CRA's react-refresh babel plugin loops on that pattern. We use
// `createElement(RuleGroup, ...)` for the recursive call instead.
function RuleGroup({ meta, node, onChange, onRemove, depth }) {
  const toggleLogic = () =>
    onChange({ ...node, logic: node.logic === "AND" ? "OR" : "AND" });

  const addRule = () =>
    onChange({
      ...node,
      rules: (node.rules || []).concat([{ field: "country", op: "equals", value: "" }]),
    });

  const addGroup = () =>
    onChange({
      ...node,
      rules: (node.rules || []).concat([
        { logic: "AND", rules: [{ field: "country", op: "equals", value: "" }] },
      ]),
    });

  const updateChild = (i, next) => {
    const rules = (node.rules || []).slice();
    rules[i] = next;
    onChange({ ...node, rules });
  };

  const removeChild = (i) => {
    const rules = (node.rules || []).slice();
    rules.splice(i, 1);
    onChange({ ...node, rules });
  };

  const borderColor =
    depth === 0 ? "border-[#0055FF]" : depth === 1 ? "border-[#7C3AED]" : "border-[#16A34A]";

  const renderChild = (child, i) => {
    if (child.logic) {
      return (
        <div key={i}>
          {createElement(RuleGroup, {
            meta,
            node: child,
            depth: depth + 1,
            onChange: (n) => updateChild(i, n),
            onRemove: () => removeChild(i),
          })}
        </div>
      );
    }
    return (
      <div key={i}>
        <RuleRow
          meta={meta}
          rule={child}
          onChange={(n) => updateChild(i, n)}
          onRemove={() => removeChild(i)}
        />
      </div>
    );
  };

  return (
    <div
      className={"border-l-2 " + borderColor + " pl-3 py-1.5 space-y-2"}
      data-testid="filter-group"
    >
      <div className="flex items-center gap-2">
        <button
          onClick={toggleLogic}
          className="h-6 px-2 text-[10px] font-bold uppercase tracking-wider bg-[#0A0A0A] text-white"
          data-testid="group-logic-toggle"
        >
          {node.logic}
        </button>
        <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
          Match {node.logic === "AND" ? "all" : "any"} of the following
        </span>
        {depth > 0 && onRemove ? (
          <button
            onClick={onRemove}
            className="ml-auto text-[#525252] hover:text-[#DC2626] p-0.5"
            data-testid="group-remove"
          >
            <Trash size={12} />
          </button>
        ) : null}
      </div>
      {(node.rules || []).map(renderChild)}
      <div className="flex gap-1.5">
        <button
          onClick={addRule}
          className="h-7 px-2 border border-[#E5E5E5] hover:border-[#0055FF] text-[10px] font-bold uppercase tracking-wider flex items-center gap-1"
          data-testid="add-rule"
        >
          <Plus size={10} weight="bold" /> Rule
        </button>
        {depth < 2 ? (
          <button
            onClick={addGroup}
            className="h-7 px-2 border border-[#E5E5E5] hover:border-[#0055FF] text-[10px] font-bold uppercase tracking-wider flex items-center gap-1"
            data-testid="add-group"
          >
            <Folder size={10} /> Group
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function SegmentBuilder({ tree, onChange }) {
  const [meta, setMeta] = useState(DEFAULT_META);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .get("/marketing/segments/filter-metadata")
      .then((r) => setMeta(Object.assign({}, DEFAULT_META, r.data)))
      .catch(() => {});
  }, []);

  const doPreview = async () => {
    setLoading(true);
    try {
      const r = await api.post("/marketing/segments/preview-tree", {
        filter_definition: tree,
      });
      setPreview(r.data);
    } catch (e) {
      const detail = e && e.response && e.response.data && e.response.data.detail;
      setPreview({ error: detail || "Preview failed" });
    }
    setLoading(false);
  };

  return (
    <div className="space-y-3" data-testid="segment-builder">
      <RuleGroup meta={meta} node={tree} onChange={onChange} depth={0} />
      <div className="flex items-center gap-3 pt-2 border-t border-[#F5F5F5]">
        <button
          onClick={doPreview}
          className="h-8 px-3 border border-[#0055FF] text-[#0055FF] text-[11px] font-bold uppercase tracking-wider"
          data-testid="preview-tree-btn"
        >
          {loading ? "…" : "Preview count"}
        </button>
        {preview && !preview.error ? (
          <p className="text-xs text-[#525252]">
            Matches <strong className="text-[#0A0A0A] tabular-nums">{preview.total}</strong> contacts.
            {preview.items && preview.items.length > 0 ? (
              <span className="text-[#A3A3A3]">
                {" "}First: {preview.items.slice(0, 3).map((i) => i.email).join(", ")}
              </span>
            ) : null}
          </p>
        ) : null}
        {preview && preview.error ? (
          <p className="text-xs text-[#DC2626]">{preview.error}</p>
        ) : null}
      </div>
    </div>
  );
}
